/**
 * GMA-1626 (CompassCare Web 11/14 — Reports hub + SITRep preview) — page
 * composition.
 *
 * Three-rail Reports hub (docs/design-mockups/CompassCare Reports.dc.html):
 * the Quick Report Builder (report-type catalog + cadence/audience toggles,
 * validated by the pure reportsModel.buildReportRequest — family audience is
 * forced to client scope and only offered for family-safe report types), the
 * NORA SITRep preview (masthead RAG from noraSitrepModel.overallStatus,
 * per-finding confidence from resolveConfidence, and the family jargon guard
 * via hasClinicianJargon — AC: no BLUF/ADL jargon in family renderings), and
 * the hub rails (Scheduled Reports + Export History).
 *
 * Presentational + fail-safe: `sitrep`/`scheduled`/`exportHistory` are
 * injected by the route (testable) and default to empty states; null /
 * malformed rows can never break the render. Colors/fonts/severity come from
 * the single design-token set. a11y: every builder control is a native
 * <button> (keyboard operable) and preview changes are announced through a
 * polite live region — the AlertsPage shipped without live regions and that
 * defect class stops here.
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import SeverityBadge from "../../components/web/SeverityBadge";
import {
  REPORT_TYPES,
  REPORT_WINDOWS,
  buildReportRequest,
} from "./reportsModel";
import {
  overallStatus,
  resolveConfidence,
  hasClinicianJargon,
} from "./noraSitrepModel";
import { formatRelativeTime } from "../../utils/webFormat";
import { theme } from "../../styles/designTokens";

// Default scope per report type (the builder derives scope "from report type",
// per the mockup); the family audience overrides to client scope because
// family viewers only ever see their own client's summaries (model rule).
const SCOPE_BY_TYPE = Object.freeze({
  daily_sitrep: "agency",
  client_summary: "client",
  group_home_summary: "group_home",
  vitals_trend: "agency",
  alert_activity: "agency",
  device_health: "agency",
  client_exception: "client",
});

const SCOPE_LABELS = Object.freeze({
  client: "Single client",
  group_home: "Group home",
  agency: "Agency-wide",
});

// Masthead RAG: model status → severity token + audience-appropriate label.
// Family language is plain ("silence is not reassurance" applies to codes too
// — RED/AMBER mean nothing to a daughter checking on her mother).
const RAG = Object.freeze({
  green: Object.freeze({ level: "normal", clinician: "GREEN", family: "DOING WELL" }),
  amber: Object.freeze({ level: "caution", clinician: "AMBER", family: "KEEPING AN EYE OUT" }),
  red: Object.freeze({ level: "critical", clinician: "RED", family: "NEEDS ATTENTION" }),
});

// Per-audience section labels (mockup copy).
const LABELS = Object.freeze({
  clinician: Object.freeze({
    bluf: "BLUF — Bottom Line Up Front",
    findings: "Top Findings",
    followups: "Recommended Follow-up",
  }),
  family: Object.freeze({
    bluf: "The short version",
    findings: "What we noticed",
    followups: "What happens next",
  }),
});

// Confidence display per audience (family gets plain language, mockup copy).
const CONFIDENCE_LABELS = Object.freeze({
  clinician: Object.freeze({ high: "HIGH", moderate: "MODERATE", low: "LOW" }),
  family: Object.freeze({
    high: "We are sure of this",
    moderate: "This is our best read",
    low: "Still gathering info",
  }),
});

// Clinical fallbacks. The family fallback must itself pass the jargon guard.
const NO_COPY_FALLBACK = "No information recorded.";
const FAMILY_SAFE_FALLBACK =
  "Your care team will share this update in plain language.";

const WINDOW_LABELS = Object.freeze({
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
});

const isRecord = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const asRows = (v) => (Array.isArray(v) ? v.filter(isRecord) : []);

const panelStyle = {
  background: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: 8,
};

const panelHeadingStyle = {
  margin: 0,
  padding: "12px 14px 8px",
  fontSize: 12,
  color: theme.textMuted,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

const toggleStyle = (pressed) => ({
  flex: 1,
  minHeight: 32,
  padding: "6px 8px",
  borderRadius: 6,
  border: `1px solid ${pressed ? theme.textMuted : theme.border}`,
  background: pressed ? theme.bg : "transparent",
  color: pressed ? theme.textPrimary : theme.textMuted,
  cursor: "pointer",
  fontFamily: theme.fontUI,
  fontSize: 12,
  fontWeight: 600,
});

export default function ReportsPage({
  sitrep,
  scheduled = [],
  exportHistory = [],
  now,
  onGenerateReport,
}) {
  const [type, setType] = useState("daily_sitrep");
  const [reportWindow, setReportWindow] = useState("daily");
  const [audience, setAudience] = useState("clinician");

  // Family viewers only ever see their own client's summaries — the model
  // rejects any other scope, so the builder derives it rather than asking.
  const scope =
    audience === "family" ? "client" : SCOPE_BY_TYPE[type] || "agency";
  const request = buildReportRequest({ type, scope, window: reportWindow, audience });

  const typeLabel = (REPORT_TYPES[type] && REPORT_TYPES[type].label) || "Report";
  const labels = LABELS[audience] || LABELS.clinician;
  const confidenceLabels = CONFIDENCE_LABELS[audience] || CONFIDENCE_LABELS.clinician;

  const preview = isRecord(sitrep) ? sitrep : null;
  const findings = preview ? asRows(preview.findings) : [];
  const followups = preview ? asRows(preview.followups) : [];
  const rag = RAG[overallStatus(findings)] || RAG.green;
  const ragLabel = audience === "family" ? rag.family : rag.clinician;

  // Prose guard: non-string/empty copy degrades to the clinical fallback; a
  // family rendering additionally swaps any jargon-carrying copy for the
  // plain-language fallback (AC — no BLUF/ADL jargon reaches families).
  const previewText = (text) => {
    if (typeof text !== "string" || text.trim() === "") return NO_COPY_FALLBACK;
    if (audience === "family" && hasClinicianJargon(text)) {
      return FAMILY_SAFE_FALLBACK;
    }
    return text;
  };

  const scheduledRows = asRows(scheduled);
  const historyRows = asRows(exportHistory);

  const generate = () => {
    if (!request.valid) return;
    if (typeof onGenerateReport === "function") onGenerateReport(request.request);
  };

  return (
    <div
      data-testid="reports-page"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(260px, 340px) minmax(0, 1fr) minmax(240px, 312px)",
        alignItems: "start",
        gap: 16,
        padding: 16,
        background: theme.bg,
        color: theme.textPrimary,
        fontFamily: theme.fontUI,
      }}
    >
      {/* ============ LEFT: quick builder + report types ============ */}
      <section
        data-testid="report-builder"
        aria-label="Quick report builder"
        style={{ display: "grid", gap: 16 }}
      >
        <div style={panelStyle}>
          <h2 style={panelHeadingStyle}>Quick Report Builder</h2>
          <div style={{ display: "grid", gap: 12, padding: "0 14px 14px" }}>
            <div role="group" aria-label="Cadence" style={{ display: "flex", gap: 6 }}>
              {REPORT_WINDOWS.map((w) => (
                <button
                  key={w}
                  type="button"
                  data-testid={`builder-window-${w}`}
                  aria-pressed={reportWindow === w}
                  onClick={() => setReportWindow(w)}
                  style={toggleStyle(reportWindow === w)}
                >
                  {WINDOW_LABELS[w] || w}
                </button>
              ))}
            </div>
            <div role="group" aria-label="Audience" style={{ display: "flex", gap: 6 }}>
              {["clinician", "family"].map((a) => (
                <button
                  key={a}
                  type="button"
                  data-testid={`builder-audience-${a}`}
                  aria-pressed={audience === a}
                  onClick={() => setAudience(a)}
                  style={toggleStyle(audience === a)}
                >
                  {a === "family" ? "Family" : "Clinician"}
                </button>
              ))}
            </div>
            <p
              data-testid="builder-scope"
              style={{ margin: 0, fontSize: 12, color: theme.textMuted }}
            >
              Scope: {SCOPE_LABELS[scope] || SCOPE_LABELS.agency}
              <span style={{ marginLeft: 6, fontSize: 10 }}>
                {audience === "family" ? "(family view)" : "(from report type)"}
              </span>
            </p>
            {!request.valid && (
              <p
                data-testid="builder-errors"
                role="alert"
                style={{ margin: 0, fontSize: 12, color: "#F0524E" }}
              >
                {request.errors.join(" · ")}
              </p>
            )}
            <button
              type="button"
              data-testid="builder-generate"
              disabled={!request.valid}
              onClick={generate}
              style={{
                minHeight: 38,
                padding: "8px 16px",
                border: "none",
                borderRadius: 6,
                background: request.valid ? "#8B7CF6" : theme.border,
                color: request.valid ? "#FFFFFF" : theme.textMuted,
                cursor: request.valid ? "pointer" : "not-allowed",
                fontFamily: theme.fontUI,
                fontSize: 13,
                fontWeight: 700,
              }}
            >
              Generate with NORA
            </button>
          </div>
        </div>

        <div style={panelStyle}>
          <h2 style={panelHeadingStyle}>Report Types</h2>
          <ul style={{ listStyle: "none", margin: 0, padding: "0 8px 8px" }}>
            {Object.keys(REPORT_TYPES).map((key) => {
              const selected = type === key;
              return (
                <li key={key}>
                  <button
                    type="button"
                    data-testid="report-type-option"
                    data-type={key}
                    aria-pressed={selected}
                    onClick={() => setType(key)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: "100%",
                      textAlign: "left",
                      margin: "0 0 4px",
                      padding: "8px 10px",
                      borderRadius: 6,
                      border: `1px solid ${selected ? theme.textMuted : "transparent"}`,
                      background: selected ? theme.bg : "transparent",
                      color: theme.textPrimary,
                      cursor: "pointer",
                      fontFamily: theme.fontUI,
                      fontSize: 13,
                    }}
                  >
                    <span style={{ flex: 1 }}>{REPORT_TYPES[key].label}</span>
                    {REPORT_TYPES[key].familyAllowed && (
                      <span style={{ fontSize: 10, color: theme.textMuted }}>
                        Family-safe
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      {/* ============ CENTER: SITRep preview ============ */}
      <section
        data-testid="reports-preview"
        aria-label="Report preview"
        style={panelStyle}
      >
        {/* Polite live region: announces builder-driven preview changes to
            assistive tech without stealing focus. */}
        <p
          data-testid="preview-status"
          role="status"
          aria-live="polite"
          style={{
            margin: 0,
            padding: "12px 16px",
            borderBottom: `1px solid ${theme.border}`,
            fontSize: 12,
            color: theme.textMuted,
          }}
        >
          {`${typeLabel} preview — ${reportWindow} window · ${audience} audience`}
          {preview ? ` · status ${ragLabel}` : ""}
        </p>

        {preview === null ? (
          <p
            data-testid="preview-empty"
            style={{ padding: 16, margin: 0, color: theme.textMuted }}
          >
            No report generated yet — pick a report type and generate with NORA.
          </p>
        ) : (
          <div style={{ display: "grid", gap: 16, padding: 16 }}>
            <div
              data-testid="preview-masthead"
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 12,
                paddingBottom: 12,
                borderBottom: `1px solid ${theme.border}`,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 style={{ margin: 0, fontSize: 15 }}>{typeLabel}</h2>
                <p style={{ margin: 0, fontSize: 11, color: theme.textMuted }}>
                  Generated by NORA
                  {formatRelativeTime(preview.generatedAt, now)
                    ? ` · ${formatRelativeTime(preview.generatedAt, now)}`
                    : ""}
                </p>
              </div>
              <SeverityBadge level={rag.level} label={ragLabel} />
            </div>

            <section aria-label={labels.bluf}>
              <h3 style={{ ...panelHeadingStyle, padding: 0, marginBottom: 4 }}>
                {labels.bluf}
              </h3>
              <p data-testid="preview-bluf" style={{ margin: 0, fontSize: 14, lineHeight: 1.6 }}>
                {previewText(preview.bluf)}
              </p>
            </section>

            <section aria-label={labels.findings}>
              <h3 style={{ ...panelHeadingStyle, padding: 0, marginBottom: 8 }}>
                {labels.findings}
              </h3>
              {findings.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: theme.textMuted }}>
                  {NO_COPY_FALLBACK}
                </p>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
                  {findings.map((finding, index) => (
                    <li
                      key={
                        typeof finding.title === "string"
                          ? `${finding.title}-${index}`
                          : index
                      }
                      data-testid="preview-finding"
                      style={{
                        border: `1px solid ${theme.border}`,
                        borderRadius: 6,
                        padding: "10px 12px",
                        background: theme.bg,
                      }}
                    >
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600 }}>
                        {typeof finding.title === "string" && finding.title.trim() !== ""
                          ? finding.title
                          : "Development"}
                      </p>
                      <p style={{ margin: "4px 0 0", fontSize: 12, color: theme.textMuted, lineHeight: 1.5 }}>
                        {previewText(finding.detail)}
                      </p>
                      <p
                        data-testid="finding-confidence"
                        style={{
                          margin: "6px 0 0",
                          fontSize: 11,
                          fontFamily: theme.fontMono,
                          color: theme.textMuted,
                        }}
                      >
                        {`Confidence: ${confidenceLabels[resolveConfidence(finding)]}`}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-label={labels.followups}>
              <h3 style={{ ...panelHeadingStyle, padding: 0, marginBottom: 8 }}>
                {labels.followups}
              </h3>
              {followups.length === 0 ? (
                <p style={{ margin: 0, fontSize: 13, color: theme.textMuted }}>
                  {NO_COPY_FALLBACK}
                </p>
              ) : (
                <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
                  {followups.map((followup, index) => (
                    <li
                      key={
                        typeof followup.text === "string"
                          ? `${followup.text.slice(0, 24)}-${index}`
                          : index
                      }
                      data-testid="preview-followup"
                      style={{ display: "flex", gap: 8, fontSize: 13, lineHeight: 1.5 }}
                    >
                      <span
                        style={{
                          flex: "0 0 auto",
                          fontSize: 10,
                          fontWeight: 700,
                          padding: "2px 8px",
                          borderRadius: 999,
                          border: `1px solid ${theme.border}`,
                          color: theme.textMuted,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {typeof followup.tag === "string" && followup.tag.trim() !== ""
                          ? followup.tag
                          : "FOLLOW-UP"}
                      </span>
                      <span style={{ flex: 1 }}>{previewText(followup.text)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}
      </section>

      {/* ============ RIGHT: scheduled + export history ============ */}
      <div style={{ display: "grid", gap: 16 }}>
        <section
          data-testid="scheduled-reports"
          aria-label="Scheduled reports"
          style={panelStyle}
        >
          <h2 style={panelHeadingStyle}>Scheduled Reports</h2>
          {scheduledRows.length === 0 ? (
            <p
              data-testid="scheduled-empty"
              style={{ padding: "0 14px 14px", margin: 0, color: theme.textMuted, fontSize: 13 }}
            >
              No scheduled reports
            </p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: "0 14px 10px" }}>
              {scheduledRows.map((row, index) => (
                <li
                  key={typeof row.name === "string" ? `${row.name}-${index}` : index}
                  data-testid="scheduled-row"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 0",
                    borderBottom: `1px solid ${theme.border}`,
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: 13 }}>
                      {typeof row.name === "string" && row.name.trim() !== ""
                        ? row.name
                        : "Scheduled report"}
                    </span>
                    {(row.cadence || row.recipients) && (
                      <span style={{ fontSize: 11, color: theme.textMuted }}>
                        {[row.cadence, row.recipients].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </span>
                  {/* text state, never color alone (a11y) */}
                  <span
                    style={{
                      fontSize: 11,
                      color: row.active === false ? theme.textMuted : "#2ECC8F",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.active === false ? "Paused" : "Active"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section
          data-testid="export-history"
          aria-label="Export history"
          style={panelStyle}
        >
          <h2 style={panelHeadingStyle}>Export History</h2>
          {historyRows.length === 0 ? (
            <p
              data-testid="export-history-empty"
              style={{ padding: "0 14px 14px", margin: 0, color: theme.textMuted, fontSize: 13 }}
            >
              No exports yet
            </p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: "0 14px 10px" }}>
              {historyRows.map((row, index) => (
                <li
                  key={typeof row.name === "string" ? `${row.name}-${index}` : index}
                  data-testid="export-row"
                  style={{ padding: "8px 0", borderBottom: `1px solid ${theme.border}` }}
                >
                  <span style={{ display: "block", fontSize: 13 }}>
                    {typeof row.name === "string" && row.name.trim() !== ""
                      ? row.name
                      : "Export"}
                  </span>
                  {row.detail && (
                    <span style={{ display: "block", fontSize: 11, color: theme.textMuted }}>
                      {row.detail}
                    </span>
                  )}
                  <span
                    style={{
                      display: "block",
                      fontSize: 10,
                      fontFamily: theme.fontMono,
                      color: theme.textMuted,
                    }}
                  >
                    {[formatRelativeTime(row.at, now), row.actor]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

ReportsPage.propTypes = {
  /**
   * The generated report preview; { generatedAt, bluf, findings: [{ title,
   * detail, priority, source, deviceOffline }], followups: [{ tag, text }] }.
   * Typed loosely (object) because the page is intentionally fail-safe for
   * null / malformed content, and a strict shape would warn for those
   * supported inputs.
   */
  sitrep: PropTypes.object,
  /** Scheduled report rows; { name, cadence, recipients, active }. */
  scheduled: PropTypes.array,
  /** Export-history rows; { name, detail, at, actor }. */
  exportHistory: PropTypes.array,
  /** Reference time for relative-age display; defaults inside the format helpers. */
  now: PropTypes.number,
  /** Called as onGenerateReport(request) with the model-normalized request. */
  onGenerateReport: PropTypes.func,
};
